import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

interface WindowCurrency {
    symbol?: string;
    code?: string;
    position?: 'before' | 'after';
}

interface CurrencyFormatOptions {
    locale?: string;
    symbol?: string;
    position?: 'before' | 'after';
}

interface NumberFormatOptions {
    locale?: string;
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
}

export function getFormattingLocale(locale?: string): string {
    const selectedLocale =
        locale ?? (typeof window !== 'undefined' ? (window as Window & { locale?: { current?: string } }).locale?.current : undefined) ?? 'pt';

    if (selectedLocale.toLowerCase().startsWith('pt')) return 'pt-PT';
    if (selectedLocale.toLowerCase().startsWith('en')) return 'en-GB';

    return selectedLocale;
}

export function formatNumber(amount: number | string, options: NumberFormatOptions = {}): string {
    const numAmount = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    const safeAmount = Number.isFinite(numAmount) ? numAmount : 0;

    return new Intl.NumberFormat(getFormattingLocale(options.locale), {
        minimumFractionDigits: options.minimumFractionDigits,
        maximumFractionDigits: options.maximumFractionDigits,
    }).format(safeAmount);
}

// Global currency formatter - uses app defaults from window.currency.
export function formatCurrency(amount: number | string | null | undefined, options: CurrencyFormatOptions = {}): string {
    const numAmount = amount == null ? 0 : typeof amount === 'string' ? parseFloat(amount) || 0 : amount;

    const currency =
        (typeof window !== 'undefined' ? (window as Window & { currency?: WindowCurrency }).currency : undefined) ??
        ({ symbol: '€', position: 'after' } satisfies WindowCurrency);
    const symbol = options.symbol ?? currency.symbol ?? '€';
    const position = options.position ?? currency.position ?? 'after';
    const formattedAmount = formatNumber(numAmount, {
        locale: options.locale,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return position === 'before' ? `${symbol}${formattedAmount}` : `${formattedAmount}\u00a0${symbol}`;
}

export function formatDate(date: string, locale?: string): string {
    return new Date(date).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
}

export function formatDateTime(date: string, locale?: string): string {
    return new Date(date).toLocaleDateString(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}
